<template>
  <Teleport to="body">
    <Transition name="command-fade">
      <div v-if="open" class="command-backdrop" @mousedown.self="close">
        <section class="command-palette" role="dialog" aria-modal="true" aria-labelledby="command-title">
          <header class="command-search">
            <i class="pi pi-search" />
            <input
              ref="searchInput"
              v-model.trim="query"
              type="search"
              placeholder="Search pages, shipments or customers..."
              aria-label="Global search"
              @keydown.down.prevent="moveSelection(1)"
              @keydown.up.prevent="moveSelection(-1)"
              @keydown.enter.prevent="openSelected"
            />
            <kbd>ESC</kbd>
          </header>

          <div class="command-body">
            <div v-if="loading" class="command-loading">
              <ProgressSpinner stroke-width="4" /><span>Searching workspace…</span>
            </div>
            <template v-else-if="resultGroups.length">
              <section v-for="group in resultGroups" :key="group.label" class="command-group">
                <h2>{{ group.label }}</h2>
                <div
                  v-for="result in group.items"
                  :key="`${result.kind}-${result.path}`"
                  class="command-result"
                  :class="{ active: flatResults[selectedIndex] === result }"
                  role="button"
                  tabindex="0"
                  @mouseenter="selectedIndex = flatResults.indexOf(result)"
                  @click="navigate(result)"
                  @keydown.enter.prevent="navigate(result)"
                >
                  <span class="command-result__icon"><i :class="result.icon" /></span>
                  <span
                    ><strong>{{ result.label }}</strong
                    ><small>{{ result.description }}</small></span
                  >
                  <button
                    v-if="result.kind === 'page'"
                    type="button"
                    class="command-pin"
                    :aria-label="isPinned(result.path) ? 'Unpin page' : 'Pin page'"
                    @click.stop="togglePinned(result)"
                  >
                    <i :class="isPinned(result.path) ? 'pi pi-star-fill' : 'pi pi-star'" />
                  </button>
                  <i v-else class="pi pi-arrow-right" />
                </div>
              </section>
            </template>
            <div v-else class="command-empty">
              <i class="pi pi-search" /><strong>No result found</strong
              ><span>Try a page name, reference number or customer name.</span>
            </div>
          </div>

          <footer>
            <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span><span><kbd>↵</kbd> Open</span
            ><span id="command-title"><kbd>⌘/Ctrl K</kbd> Global search</span>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { navigationModel } from '@/layout/navigationModel';
import { ReferenceService } from '@/service/ReferenceService';
import { CustomerService } from '@/service/CustomerService';
import { usePermissions } from '@/composables/usePermissions';
import { useWorkspacePreferences } from '@/composables/useWorkspacePreferences';
import { useAppI18n } from '@/i18n';
import emitter from '@/utils/eventBus';
import { formatPartyName } from '@/utils/globalHelpers';

const router = useRouter();
const { can } = usePermissions();
const { locale, t } = useAppI18n();
const { recent, isPinned, togglePinned, trackUsage } = useWorkspacePreferences();
const referenceService = new ReferenceService();
const customerService = new CustomerService();
const searchInput = ref(null);
const open = ref(false);
const query = ref('');
const loading = ref(false);
const remoteResults = ref({ shipments: [], customers: [] });
const selectedIndex = ref(0);
let searchTimer;
let requestSequence = 0;

const flattenPages = (items, path = []) =>
  items.flatMap((item) => {
    if (item.visibleInMenu === false || (item.permission && !can(item.permission))) return [];
    const nextPath = [...path, item.label];
    if (item.items) return flattenPages(item.items, nextPath);
    if (!item.to) return [];
    const resolved = router.resolve(item.to);
    return [
      {
        kind: 'page',
        label: t(item.label),
        description: path.map(t).join(' / '),
        path: resolved.fullPath,
        icon: `fa:${item.icon || 'link'}`,
        faIcon: item.icon,
        permission: item.permission
      }
    ];
  });

const pages = computed(() => flattenPages(navigationModel));
const pageResults = computed(() => {
  const search = query.value.toLocaleLowerCase(locale.value);
  const source = search
    ? pages.value.filter((page) => `${page.label} ${page.description}`.toLocaleLowerCase(locale.value).includes(search))
    : recent.value.map((item) => ({
        ...item,
        kind: 'recent',
        description: t('Recently visited'),
        icon: 'pi pi-history'
      }));
  return source.slice(0, 6).map((item) => ({ ...item, icon: item.icon?.startsWith('pi ') ? item.icon : 'pi pi-file' }));
});

const resultGroups = computed(() =>
  [
    { label: query.value ? t('Pages') : t('Recent pages'), items: pageResults.value },
    { label: t('Shipments'), items: remoteResults.value.shipments },
    { label: t('Customers'), items: remoteResults.value.customers }
  ].filter((group) => group.items.length)
);
const flatResults = computed(() => resultGroups.value.flatMap((group) => group.items));

const tableQuery = (value, columns) => ({
  page: 0,
  rows: 5,
  filters: {
    global: { value, matchMode: 'contains' },
    ...Object.fromEntries(
      columns.map((key) => [key, { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }])
    )
  }
});

const searchRemote = async (value) => {
  const sequence = ++requestSequence;
  if (value.length < 2) {
    remoteResults.value = { shipments: [], customers: [] };
    loading.value = false;
    return;
  }
  loading.value = true;
  const requests = [];
  requests.push(
    can('shipments.view')
      ? referenceService.getReferences(tableQuery(value, ['order_number', 'sender.company_name']))
      : Promise.resolve(null)
  );
  requests.push(
    can('customers.view')
      ? customerService.getCustomers(tableQuery(value, ['first_name', 'last_name', 'company_name', 'mobile']))
      : Promise.resolve(null)
  );
  const [shipments, customers] = await Promise.allSettled(requests);
  if (sequence !== requestSequence) return;
  const shipmentRows = shipments.status === 'fulfilled' ? shipments.value?.data?.data?.data || [] : [];
  const customerRows = customers.status === 'fulfilled' ? customers.value?.data?.data?.data || [] : [];
  remoteResults.value = {
    shipments: shipmentRows
      .slice(0, 5)
      .map((item) => ({
        kind: 'shipment',
        label: item.order_number || `#${item.id}`,
        description: item.sender ? formatPartyName(item.sender) : item.sender_company_name || t('Shipment'),
        path: router.resolve({ name: 'ReferencesTracking', params: { id: item.id } }).fullPath,
        icon: 'pi pi-box'
      })),
    customers: customerRows
      .slice(0, 5)
      .map((item) => ({
        kind: 'customer',
        label: formatPartyName(item),
        description: item.mobile || item.email || t('Customer'),
        path: router.resolve({ name: 'CustomersCenter', params: { id: item.id } }).fullPath,
        icon: 'pi pi-user'
      }))
  };
  loading.value = false;
  trackUsage('global_search', value);
};

watch(query, (value) => {
  clearTimeout(searchTimer);
  selectedIndex.value = 0;
  searchTimer = setTimeout(() => searchRemote(value), 300);
});
watch(flatResults, () => {
  if (selectedIndex.value >= flatResults.value.length) selectedIndex.value = 0;
});

const show = () => {
  open.value = true;
  trackUsage('command_open');
  nextTick(() => searchInput.value?.focus());
};
const close = () => {
  open.value = false;
  query.value = '';
  remoteResults.value = { shipments: [], customers: [] };
};
const navigate = (result) => {
  trackUsage('command_navigate', result.kind);
  router.push(result.path);
  close();
};
const moveSelection = (amount) => {
  const length = flatResults.value.length;
  if (length) selectedIndex.value = (selectedIndex.value + amount + length) % length;
};
const openSelected = () => {
  const result = flatResults.value[selectedIndex.value];
  if (result) navigate(result);
};
const handleShortcut = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    open.value ? close() : show();
  } else if (event.key === 'Escape' && open.value) close();
};

onMounted(() => {
  window.addEventListener('keydown', handleShortcut);
  emitter.on('open-global-search', show);
});
onBeforeUnmount(() => {
  clearTimeout(searchTimer);
  window.removeEventListener('keydown', handleShortcut);
  emitter.off('open-global-search', show);
});
</script>

<style scoped>
.command-backdrop {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: grid;
  align-items: start;
  justify-items: center;
  padding: min(12vh, 7rem) 1rem 1rem;
  background: rgb(15 23 42 / 48%);
  backdrop-filter: blur(4px);
}
.command-palette {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(720px, 100%);
  max-height: min(680px, 78vh);
  overflow: hidden;
  border: 1px solid var(--surface-border);
  border-radius: 18px;
  color: var(--text-color);
  background: var(--surface-card);
  box-shadow: 0 28px 70px rgb(15 23 42 / 30%);
}
.command-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 4.2rem;
  padding: 0 1.15rem;
  border-bottom: 1px solid var(--surface-border);
}
.command-search > i {
  color: var(--primary-color);
  font-size: 1.15rem;
}
.command-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--text-color);
  background: transparent;
  font-size: 1rem;
}
kbd {
  padding: 0.18rem 0.38rem;
  border: 1px solid var(--surface-border);
  border-radius: 5px;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
  font: inherit;
  font-size: 0.7rem;
}
.command-body {
  min-height: 16rem;
  overflow-y: auto;
  padding: 0.7rem;
}
.command-group h2 {
  margin: 0.65rem 0.55rem 0.35rem;
  color: var(--text-color-secondary);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.command-result {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.65rem 0.7rem;
  border: 0;
  border-radius: 10px;
  color: var(--text-color);
  background: transparent;
  text-align: start;
  cursor: pointer;
}
.command-result.active,
.command-result:hover {
  background: var(--surface-hover);
}
.command-result__icon {
  display: grid;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 9px;
  color: var(--primary-color);
  background: var(--primary-50);
  place-items: center;
}
.command-result > span:nth-child(2) {
  display: grid;
  gap: 0.2rem;
}
.command-result small {
  color: var(--text-color-secondary);
}
.command-pin {
  border: 0;
  color: var(--primary-color);
  background: transparent;
  cursor: pointer;
}
.command-loading,
.command-empty {
  display: grid;
  min-height: 15rem;
  place-content: center;
  justify-items: center;
  gap: 0.65rem;
  color: var(--text-color-secondary);
  text-align: center;
}
.command-empty > i {
  font-size: 1.8rem;
}
.command-empty strong {
  color: var(--text-color);
}
.command-palette footer {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.65rem 1rem;
  border-top: 1px solid var(--surface-border);
  color: var(--text-color-secondary);
  background: var(--surface-ground);
  font-size: 0.72rem;
}
.command-palette footer span:last-child {
  margin-inline-start: auto;
}
.command-fade-enter-active,
.command-fade-leave-active {
  transition: opacity 0.15s ease;
}
.command-fade-enter-from,
.command-fade-leave-to {
  opacity: 0;
}
@media (max-width: 600px) {
  .command-backdrop {
    padding: 0;
  }
  .command-palette {
    width: 100%;
    max-height: 100dvh;
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
  }
  .command-palette footer {
    display: none;
  }
}
</style>
